import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9nit3obdkpk99j7gk9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2018', title: '平台诞生', desc: '成全影视正式成立，致力于为华语观众提供优质的免费在线追剧服务。' },
    { year: '2019', title: '内容拓展', desc: '平台内容从国产剧拓展到韩剧、美剧、日剧等多个品类，满足不同观众的追剧需求。' },
    { year: '2020', title: '技术升级', desc: '全面升级播放技术，实现高清不缓冲播放体验，用户量突破百万。' },
    { year: '2021', title: '生态完善', desc: '推出文章资讯板块，为用户提供深度的影视评论和追剧指南内容。' },
    { year: '2023', title: '口碑爆发', desc: '凭借优质内容和流畅体验，成全影视成为最受欢迎的免费追剧平台之一。' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '成全影视需要付费吗？',
        acceptedAnswer: { '@type': 'Answer', text: '不需要。成全影视所有电视剧和电影完全免费，无需开通VIP会员，无需付费即可观看所有内容。' },
      },
      {
        '@type': 'Question',
        name: '成全影视支持哪些设备？',
        acceptedAnswer: { '@type': 'Answer', text: '成全影视支持电脑、平板、手机等所有主流设备，只需浏览器即可观看，无需下载任何软件。' },
      },
      {
        '@type': 'Question',
        name: '电视剧更新速度快吗？',
        acceptedAnswer: { '@type': 'Answer', text: '我们会在电视剧播出后第一时间更新最新剧集，确保您不错过任何精彩内容。热门剧集会保持每日更新的频率。' },
      },
      {
        '@type': 'Question',
        name: '为什么选择成全影视？',
        acceptedAnswer: { '@type': 'Answer', text: '成全影视拥有海量电视剧资源、高清流畅的播放体验、无需付费的免费模式，以及覆盖多个国家和地区的丰富内容库，是追剧爱好者的最佳选择。' },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '成全影视',
    url: 'https://alu-expo-1.cn/',
    description: '成全影视提供电视剧全集播放，不缓冲看剧免费在线观看，全集免费电视剧流畅追剧完整版。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@alu-expo-1.cn',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <title>关于我们 - 成全影视 | 品牌故事与联系方式</title>
      <meta name="description" content="成全影视成立于2018年，提供电视剧全集播放、不缓冲看剧免费在线观看服务。了解成全影视的品牌故事、发展历程和联系方式。涵盖国产剧、韩剧、美剧、日剧等分类。" />
      <meta name="keywords" content="成全影视,关于我们,品牌故事,联系方式,免费追剧平台" />
      <link rel="canonical" href="https://alu-expo-1.cn/about" />
      <meta property="og:title" content="关于我们 - 成全影视 | 品牌故事与联系方式" />
      <meta property="og:description" content="成全影视成立于2018年，提供电视剧全集播放、不缓冲看剧免费在线观看服务。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://alu-expo-1.cn/about" />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-[1040px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="mt-8 mb-14">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4">关于成全影视</h1>
              <p className="text-base text-foreground-500 max-w-[650px] mx-auto leading-relaxed">
                成全影视成立于2018年，是一站式免费在线追剧平台。我们致力于为全球华语观众提供最丰富的电视剧资源和最佳的观影体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-eye-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-3xl font-bold text-foreground-900 mb-1">500万+</h3>
                <p className="text-sm text-foreground-500">月活跃用户</p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-film-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="text-3xl font-bold text-foreground-900 mb-1">10,000+</h3>
                <p className="text-sm text-foreground-500">影视资源</p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-global-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="text-3xl font-bold text-foreground-900 mb-1">50+</h3>
                <p className="text-sm text-foreground-500">覆盖国家/地区</p>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 text-center">品牌故事</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className={`flex flex-col md:flex-row gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                        <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded mb-2">{m.year}</span>
                        <h3 className="text-lg font-bold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-0 justify-center">
                      <div className="w-3 h-3 bg-primary-500 rounded-full mt-6 shrink-0 relative z-10"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '追剧达人小王', text: '成全影视真的太方便了！不用开VIP就能看所有剧，画质也很清晰，追剧体验超棒！' },
                { name: '影视爱好者老李', text: '资源很全，更新的速度也很快。现在每天都会打开成全影视看看有没有新剧上线。' },
                { name: '职场妈妈小陈', text: '下班回家就喜欢追剧放松，成全影视不卡顿、不缓冲，体验特别好，强烈推荐！' },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{review.name[0]}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14" id="faq">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 text-center">常见问题</h2>
            <div className="space-y-3 max-w-[750px] mx-auto">
              {[
                { q: '成全影视需要付费吗？', a: '不需要。成全影视所有电视剧和电影完全免费，无需开通VIP会员，无需付费即可观看所有内容。' },
                { q: '成全影视支持哪些设备？', a: '成全影视支持电脑、平板、手机等所有主流设备，只需浏览器即可观看，无需下载任何软件。' },
                { q: '电视剧更新速度快吗？', a: '我们会在电视剧播出后第一时间更新最新剧集，确保您不错过任何精彩内容。热门剧集会保持每日更新的频率。' },
                { q: '为什么选择成全影视？', a: '成全影视拥有海量电视剧资源、高清流畅的播放体验、无需付费的免费模式，以及覆盖多个国家和地区的丰富内容库，是追剧爱好者的最佳选择。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 border border-background-200/60 rounded-xl group">
                  <summary className="px-5 py-4 text-base font-medium text-foreground-800 cursor-pointer flex items-center justify-between">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-14" id="contact">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@alu-expo-1.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-accent-100 rounded-lg">
                      <i className="ri-customer-service-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h3>
                      <p className="text-sm text-foreground-500">7×24小时在线，随时为您解答问题</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                      <i className="ri-question-answer-line text-lg text-secondary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">反馈建议</h3>
                      <p className="text-sm text-foreground-500">欢迎通过右侧表单提交您的宝贵意见</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-50 border border-background-200/60 rounded-xl p-5 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <div className="honeypot-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer"
                  >
                    提交留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}